import type {
    AdminAssignItemPayload,
    AdminAuthResponse,
    AdminCreateCasePayload,
    AdminCreateItemPayload,
    AdminItem,
    AdminUpdateCasePayload,
    AdminUpdateItemPayload,
    CaseItemsResponse,
    CaseSummary,
} from '~/lib/contracts';

const readJson = async <TData>(response: Response) => {
    const payload = (await response.json().catch(() => null)) as
        | { error?: string }
        | TData
        | null;

    if (!response.ok) {
        if (payload && typeof payload === 'object' && 'error' in payload) {
            throw new Error(payload.error ?? `Request failed with status ${response.status}`);
        }

        throw new Error(`Request failed with status ${response.status}`);
    }

    return payload as TData;
};

export const getCases = async () => {
    const response = await fetch('/api/cases');
    return readJson<CaseSummary[]>(response);
};

export const getCaseItems = async (caseId: number) => {
    const response = await fetch(`/api/cases/${caseId}/items`);
    return readJson<CaseItemsResponse>(response);
};

export const getAdminMe = async () => {
    const response = await fetch('/api/admin/auth/me');
    return readJson<AdminAuthResponse>(response);
};

export const loginAdmin = async (email: string, password: string) => {
    const response = await fetch('/api/admin/auth/login', {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
    });

    return readJson<AdminAuthResponse>(response);
};

export const logoutAdmin = async () => {
    const response = await fetch('/api/admin/auth/logout', {
        method: 'POST',
    });

    return readJson<{ ok: true }>(response);
};

export const getAdminItems = async () => {
    const response = await fetch('/api/admin/items');
    return readJson<AdminItem[]>(response);
};

export const createAdminItem = async (payload: AdminCreateItemPayload) => {
    const response = await fetch('/api/admin/items', {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    return readJson<AdminItem>(response);
};

export const updateAdminItem = async (itemId: number, payload: AdminUpdateItemPayload) => {
    const response = await fetch(`/api/admin/items/${itemId}`, {
        method: 'PATCH',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    return readJson<AdminItem>(response);
};

export const deleteAdminItem = async (itemId: number) => {
    const response = await fetch(`/api/admin/items/${itemId}`, {
        method: 'DELETE',
    });

    return readJson<{ ok: true }>(response);
};

export const uploadAdminItemImage = async (itemId: number, file: File) => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`/api/admin/items/${itemId}/image`, {
        method: 'POST',
        body: formData,
    });

    return readJson<AdminItem>(response);
};

export const getAdminCases = async () => {
    const response = await fetch('/api/admin/cases');
    return readJson<CaseSummary[]>(response);
};

export const createAdminCase = async (payload: AdminCreateCasePayload) => {
    const response = await fetch('/api/admin/cases', {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    return readJson<CaseSummary>(response);
};

export const updateAdminCase = async (caseId: number, payload: AdminUpdateCasePayload) => {
    const response = await fetch(`/api/admin/cases/${caseId}`, {
        method: 'PATCH',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    return readJson<CaseSummary>(response);
};

export const deleteAdminCase = async (caseId: number) => {
    const response = await fetch(`/api/admin/cases/${caseId}`, {
        method: 'DELETE',
    });

    return readJson<{ ok: true }>(response);
};

export const assignAdminCaseItem = async (
    caseId: number,
    itemId: number,
    payload: AdminAssignItemPayload,
) => {
    const response = await fetch(`/api/admin/cases/${caseId}/items/${itemId}`, {
        method: 'PUT',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    return readJson<{ ok: true }>(response);
};

export const unassignAdminCaseItem = async (caseId: number, itemId: number) => {
    const response = await fetch(`/api/admin/cases/${caseId}/items/${itemId}`, {
        method: 'DELETE',
    });

    return readJson<{ ok: true }>(response);
};
